import { Row } from "react-bootstrap";
import ReactPlayer from "react-player";
import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import LoginModal from "../components/LoginModal"; // Import the LoginModal component

const UseCasesPage = () => {
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [defaultTab, setDefaultTab] = useState("register");
    const [activeSection, setActiveSection] = useState(0);
    const sectionsRef = useRef([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const index = sectionsRef.current.indexOf(entry.target);
                        setActiveSection(index);
                        entry.target.classList.add('active');
                    }
                });
            },
            {
                threshold: 0.5,
            }
        );

        sectionsRef.current.forEach((section) => {
            if (section) observer.observe(section);
        });

        return () => observer.disconnect();
    }, []);

    const useCases = [
        {
            title: "SnorkleLabs for education and presentations",
            description: "Convert traditional slide decks into interactive, mobile-activating experiences using either Snorkle's slides function or through a free add-in for Microsoft PowerPoint. For each visual created, Snorkle automatically generates QR codes that link to mobile-friendly, interactive visuals that give attendees the same amount of control and inspection capabilities as the presenter.",
            videoUrl: "https://vimeo.com/1067867379",
            link: "/powerpoint",
            contactLink: true
        },
        {
            title: "Platform (and language direction) agnostic",
            description: "Break language and platform barriers with visual communication. Our platform makes interactive 3D exploration accessible on any device, in any language. Whether you're annotating molecular structures in Hebrew, Arabic, or any other script, your insights remain clear and intuitive. From desktops to mobile devices, our technology ensures seamless collaboration—because science speaks a universal language.",
            videoUrl: "https://vimeo.com/1067878765",
            contactLink: true
        },
        {
            title: "Share your research: Import directly from popular analysis platforms",
            description: "Seamlessly share and cite your research. Import your work directly from Imaris, PyMOL, ImageJ, DICOM viewers, and more—whether you're analyzing two-photon microscopy stacks, molecular structures, or medical imagery. Obtain citable DOIs with Snorkle (through Crossref), making it simple to add interactive visuals to your publication list. Focus on discovery, not file compatibility.",
            videoUrl: "https://vimeo.com/1067891367",
            contactLink: true
        },
        {
            title: "Integrate interactive science into your social media campaigns",
            description: "Bring your science to life on social media. Turn static posts into interactive experiences by embedding volumetric renderings, confocal image stacks, and 3D models directly into your Twitter, LinkedIn, and other platforms. Engage your audience with science they can explore, rotate, and zoom—right from their feed.",
            imageUrl: "https://snorkle-output-files.s3.amazonaws.com/tweet_image.png",
            contactLink: true
        },
        {
            title: "Talk through data live or create screen recordings",
            description: "Bring your data to life with live narration and screen recording. Whether you're a radiologist explaining an MRI, a researcher presenting findings, or an educator guiding students, our platform lets you talk through complex visuals in real time. Capture and share interactive sessions with picture-in-picture video, so your audience can see both you and the data. Perfect for remote collaboration, teaching, and scientific storytelling—seamlessly communicate insights with clarity and impact.",
            imageUrl: "https://snorkle-visuals.s3.us-east-1.amazonaws.com/screen-recording1.png",
            beta: true
        },
        {
            title: "Create a virtual museum exhibit starting with just a single file",
            description: "With our powerful scientific visualization tools, you can annotate, markup, and transform your 3D fossil scans or downloads from Morphosource and other paleontology repositories into interactive 3D experiences—perfect for kiosks, touchscreen displays, and online galleries. You can even add voiceovers! Engage audiences with dynamic storytelling, allowing them to explore complex concepts with ease. No coding required—just upload, enhance, and showcase.",
            videoUrl: "https://vimeo.com/1067900567",
            contactLink: true
        },
        {
            title: "Become a molecular storyteller",
            description: "Become a molecular storyteller. Unlock the power of interactive protein visualizations to bring your science outreach to life. Highlight individual residues, track their position in the sequence, and narrate the story behind every molecular interaction. Create engaging, educational content that connects the dots between structure and function in a way that's easy to understand and visually captivating.",
            videoUrl: "https://vimeo.com/1068169832",
            beta: true
        },
        {
            title: "Embed branded, interactive content into your website",
            description: "Offer engaging, interactive visuals on your group's website. Each Snorkle visual can be embedded simply by copying and pasting html code snippets.",
            imageUrl: "https://snorkle-output-files.s3.amazonaws.com/lab_webpage_embed-use-case-2monitor.png",
            contactLink: true
        },
        {
            title: "Deliver verified, interactive visual explainers",
            description: "Whether it's for the purpose of giving your practice's waiting room experience a 3D makeover, supplementing teaching, or creating marketing materials, build amazing, accurate scientific experiences.",
            images: [
                "https://snorkle-output-files.s3.amazonaws.com/mobiledisplays_usecase_all.png",
                "https://snorkle-visuals.s3.us-east-1.amazonaws.com/waiting-room-menu.png"
            ],
            contactLink: true
        }
    ];

    return (
        <>
            <Row className="justify-content-center">
                <div className="main-container-narrower">
                    {useCases.map((useCase, index) => (
                        <div
                            key={index}
                            ref={el => sectionsRef.current[index] = el}
                            className="use-case-section scroll-reveal"
                        >
                            <div className="modern-card">
                                <h2 className="TitleText mb-4">
                                    {index === 0 ? (
                                        <>
                                            Snorkle<sup><i>Labs</i></sup> for education and presentations
                                        </>
                                    ) : (
                                        useCase.title
                                    )}
                                </h2>
                                <p>{useCase.description}</p>

                                {useCase.videoUrl && (
                    <div className="use-case-video mb-4">
                        <ReactPlayer 
                                            url={useCase.videoUrl}
                            width="100%" 
                            height="450px" 
                            playing={false} 
                            controls={true}
                            muted={true} 
                            loop={true} 
                                            title={`${useCase.title} demonstration video`}
                        />
                    </div>
                                )}

                                {useCase.imageUrl && (
                                    <div className="text-center mb-4">
                                        <img 
                                            src={useCase.imageUrl} 
                                            alt={`${useCase.title} demonstration`}
                                            style={{ maxWidth: '75%' }}
                        />
                    </div>
                                )}

                                {useCase.images && (
                                    <div className="text-center mb-4">
                                        {useCase.images.map((imgUrl, imgIndex) => (
                                            <img 
                                                key={imgIndex}
                                                src={imgUrl} 
                                                alt={`${useCase.title} demonstration ${imgIndex + 1}`}
                                                style={{ maxWidth: '75%', marginBottom: '1rem' }}
                        />
                                        ))}
                    </div>
                                )}

                                {/* Action buttons always below media */}
                                <div className="use-case-actions mt-4 d-flex flex-wrap gap-2">
                                    {useCase.link && (
                                        <Link to={useCase.link} className="modern-button">
                                            Learn more
                                        </Link>
                                    )}
                                    {useCase.contactLink && (
                                        <Link to="/contact" className="modern-button">
                                            Contact us
                                        </Link>
                                    )}
                                    {useCase.beta && (
                                        <span className="ms-3" style={{ color: "#B0B0B0", alignSelf: 'center' }}>
                                            (Beta access, by invite.)
                                        </span>
                                    )}
                                </div>
                    </div>
                    </div>
                    ))}
                </div>
            </Row>

            {/* The LoginModal component */}
            <LoginModal
                isOpen={showLoginModal}
                onClose={() => {
                    setShowLoginModal(false);
                }}
                onClosed={() => {
                    setTimeout(() => { document.activeElement.blur(); }, 10)
                }}
                defaultTab={defaultTab}
            />
        </>
    );
};

export default UseCasesPage;
